import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Globe, KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "../ui/card";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { RoomMenu } from "./RoomMenu";
import { prefetchCallOnIntent } from "../../room/active-call";
import { UserAvatar, OverlappingUserAvatar } from "./UserAvatar";
import type { Room } from "../../lib/rooms";

const roomMeta = "text-xs leading-4.5 font-normal text-muted-foreground";
// Only public rooms get a visibility label; the Public rooms tab lists nothing
// else, so it hides the label.
export function RoomCard({
  room,
  showVisibility = true,
}: {
  room: Room;
  showVisibility?: boolean;
}) {
  const { t } = useTranslation();
  const avatars = useRef<HTMLDivElement>(null);
  const [slots, setSlots] = useState(6);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setSlots(Math.max(1, Math.floor((entry.contentRect.width - 8) / 24))),
    );
    if (avatars.current) observer.observe(avatars.current);
    return () => observer.disconnect();
  }, []);
  const count = room.participants.length;
  const shown = count > slots ? Math.max(0, slots - 1) : count;
  const overflow = room.participants.slice(shown);
  const occupancy = t("home.occupancyLabel", { count, max: room.maxParticipants });
  return (
    <RoomMenu room={room}>
      <Card className="relative min-w-0 gap-0 rounded-xl border-0 p-0 shadow-none transition-[background] duration-160 focus-within:outline-2 focus-within:outline-offset-3 focus-within:outline-ring focus-within:outline-solid hover:bg-secondary motion-reduce:transition-none">
        <Link
          className="px-5 pt-5.5 text-inherit no-underline outline-none after:absolute after:inset-0 after:rounded-xl"
          to="/room/$roomId"
          params={{ roomId: room.id }}
          {...prefetchCallOnIntent}
        >
          <h2 className="flex items-center gap-2.25 font-heading text-base font-semibold">
            {room.hasPassword ? (
              <KeyRound
                className="size-4 flex-none text-muted-foreground"
                role="img"
                aria-label={t("home.passcodeRequired")}
              />
            ) : null}
            <span className="truncate">{room.name}</span>
          </h2>
          <p
            className={`mt-1.75 flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap ${roomMeta}`}
          >
            <span className="overflow-hidden text-ellipsis">
              {t("home.owner", { name: room.ownerName })}
            </span>
            {showVisibility && room.visibility === "public" ? (
              <span
                className={`flex flex-none items-center gap-1 before:mr-1 before:content-['·'] ${roomMeta}`}
              >
                <Globe className="size-3" aria-hidden="true" />
                {t("home.publicShort")}
              </span>
            ) : null}
          </p>
          <span className="sr-only">{t("home.join")}</span>
        </Link>
        <div className="mt-6.5 flex items-center gap-2.5 px-5 pb-5 max-[30rem]:mt-5.5">
          <div ref={avatars} className="flex h-8 min-w-0 flex-1 items-center">
            {!count && room.presenceAvailable ? (
              <span
                className="inline-flex h-8 items-center [&>:nth-child(2)]:opacity-65 [&>:nth-child(3)]:opacity-35"
                role="img"
                aria-label={t("home.noParticipants")}
              >
                {[0, 1, 2].map((index) => (
                  <OverlappingUserAvatar
                    key={index}
                    index={index}
                    hasNext={index < 2}
                    aria-hidden="true"
                  />
                ))}
              </span>
            ) : null}
            {room.participants.slice(0, shown).map((person, index) => (
              <OverlappingUserAvatar
                key={person.id}
                index={index}
                user={person}
                hasNext={index < shown - 1 || Boolean(overflow.length)}
              />
            ))}
            {overflow.length ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="relative z-1 -ml-2 flex h-8 min-w-8 flex-none cursor-pointer items-center justify-center rounded-full bg-accent px-1.25 text-xs text-zinc-200"
                    type="button"
                    aria-label={t("home.more", { count: overflow.length })}
                  >
                    +{overflow.length}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-62.5" align="start">
                  {overflow.map((person) => (
                    <DropdownMenuLabel key={person.id} className="flex items-center gap-2">
                      <UserAvatar user={person} />
                      <span className="wrap-anywhere">{person.name}</span>
                    </DropdownMenuLabel>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
          {/* Only limited rooms show their occupancy; the avatars cover the rest. */}
          {room.maxParticipants ? (
            <span
              className={`flex-none whitespace-nowrap tabular-nums ${roomMeta}`}
              title={occupancy}
              aria-label={occupancy}
            >
              {room.presenceAvailable ? count : "–"}/{room.maxParticipants}
            </span>
          ) : null}
          {!room.presenceAvailable ? (
            <span className="sr-only">{t("home.presenceUnavailable")}</span>
          ) : null}
        </div>
      </Card>
    </RoomMenu>
  );
}
